import * as React from "react";
import ResponsiveImage from "~/components/responsive-image/responsive-image";
import Tag from "~/components/tag/tag";
import TruncateMarkup from "react-truncate-markup";
import classNames from "classnames";

export enum TileType {
    COL__1 = "col-1",
    COL__2 = "col-2",
    COL__4 = "col-4",
    LIST = "list"
}

const tileTypes = {
    [TileType.COL__1]: {
        // Size 423px is needed for desktop, since col-1 can be both 25% and 33% wide
        srcSetSizes: [607, 402, 518, 423],
        sizes: `(max-width: 639px) 607px, (max-width: 867px) 402px, (max-width: 1099px) 518px, 423px`
    },
    [TileType.COL__2]: {
        // Size 867px is needed since template 3 and 4 show col-2 in 100% on this breakpoint
        srcSetSizes: [607, 835, 518, 651],
        sizes: `(max-width: 639px) 607px, (max-width: 867px) 835px, (max-width: 1099px) 518px, 651px`
    },
    [TileType.COL__4]: {
        srcSetSizes: [607, 835, 671, 838],
        sizes: "(max-width: 639px) 607px, (max-width: 867px) 835px, (max-width: 1099px) 671px, 838px"
    },
    [TileType.LIST]: {
        srcSetSizes: [607, 314, 195, 245],
        sizes: `(max-width: 639px) 607px, (max-width: 867px) 314px, (max-width: 1099px) 195px, 245px`
    }
};

export interface ITeaserTile {
    imageSrc?: string;
    imageAltText?: string;
    tag?: string;
    title?: string; // Recommended max: 28 characters
    subtitle?: string; // Recommended max: 30 characters
    richText?: string; // Recommended max: 262 characters
    pageUrl?: string;
    pageTarget?: string;
    truncateTitle?: boolean;
    hideRichText?: boolean;
    tileType?: TileType;
    date?: string;
}

const TeaserTile = ({
    imageSrc,
    imageAltText,
    tag,
    title,
    subtitle,
    richText,
    pageUrl,
    pageTarget,
    truncateTitle,
    hideRichText,
    date,
    ...props
}: ITeaserTile) => {
    const tileType = props.tileType || TileType.COL__1;
    const baseClass = "teaser-tile";
    const rootClasses = classNames(baseClass, [`${baseClass}--tile-type-${tileType}`]);

    return (
        <div className={rootClasses}>
            {imageSrc && (
                <a className={`${baseClass}__image-wrapper`} href={pageUrl} target={pageTarget}>
                    <div className={`${baseClass}__image-container`}>
                        <ResponsiveImage
                            srcUrl={imageSrc}
                            defaultSize={639}
                            srcSetSizes={tileTypes[tileType].srcSetSizes}
                            sizes={tileTypes[tileType].sizes}
                            alt={imageAltText}
                        />
                    </div>
                </a>
            )}
            <div className={`${baseClass}__content-wrapper`}>
                <div className={`${baseClass}__content-container`}>
                    {tag && <Tag className={`${baseClass}__tag`}>{tag}</Tag>}

                    {props.tileType !== TileType.LIST ? (
                        <p className={`${baseClass}__date`}>{date ?? <>&nbsp;</>}</p>
                    ) : (
                        date && <p className={`${baseClass}__date`}>{date}</p>
                    )}

                    {title && (
                        <a className={`${baseClass}__title-link`} href={pageUrl} target={pageTarget}>
                            {truncateTitle ? (
                                <TruncateMarkup lines={2}>
                                    <h3
                                        className={`${baseClass}__title h3-style`}
                                        dangerouslySetInnerHTML={{ __html: title }}
                                    />
                                </TruncateMarkup>
                            ) : (
                                <h3
                                    className={`${baseClass}__title h3-style`}
                                    dangerouslySetInnerHTML={{ __html: title }}
                                />
                            )}
                        </a>
                    )}

                    {subtitle && <p className={`${baseClass}__subtitle`}>{subtitle}</p>}

                    {!hideRichText && richText && (
                        <div
                            className={`${baseClass}__rich-text`}
                            dangerouslySetInnerHTML={{ __html: richText }}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default TeaserTile;
